/**
 * bh-list-restore.js — put the customer back where they were after list → product → back.
 *
 * Two halves of one job:
 *
 *   On a list page: remember {url, scrollY} on the way out, and on return rebuild the
 *   list. pagination.js rewrites the URL to ?page=N as Load More appends pages, so a
 *   back navigation lands on ?page=N with only that single page rendered; this refetches
 *   pages 1..N-1, prepends them, and restores the offset.
 *
 *   On a product page: rewrite the collection breadcrumb to point at the exact list URL
 *   the customer left, ?page and all. A history-based breadcrumb, so the one labelled
 *   control that says where it goes actually goes there, instead of dumping them at the
 *   top of page 1. Deliberately the breadcrumb only: clicking a category in the nav is a
 *   fresh visit and should behave like one.
 *
 * Refetching rather than snapshotting the DOM into sessionStorage is deliberate: a
 * snapshot would happily show yesterday's price or a sold-out cake as available.
 *
 * Nothing here is load-bearing — every failure path leaves the customer on page N,
 * which is what they'd get without this file at all.
 *
 * ceiling: 6 pages (~90 products). Deeper lists land on page N and log it rather than
 * firing a dozen parallel requests on a phone.
 */
(function () {
  // Loaded by the breadcrumbs section AND by the grid/search sections, so on a collection
  // page the same src is emitted twice and would otherwise run twice.
  if (window.__bhListRestore) return;
  window.__bhListRestore = true;

  const KEY = 'bh-list-position';
  const RESUME = 'bh-list-resume';
  const MAX_PAGES = 6;

  // Private mode, quota, and blocked site data all throw. Restoring is a nicety; never
  // let it break the page.
  const read = (key) => { try { return sessionStorage.getItem(key); } catch (e) { return null; } };
  const write = (key, value) => { try { sessionStorage.setItem(key, value); } catch (e) { /* ignore */ } };
  const drop = (key) => { try { sessionStorage.removeItem(key); } catch (e) { /* ignore */ } };

  let saved = null;
  try { saved = JSON.parse(read(KEY) || 'null'); } catch (e) { /* ignore */ }

  // Instant, positional scroll: html { scroll-behavior: smooth } (app.css) would animate
  // the jump, and behavior:'instant' throws on older Safari — which would skip the restore
  // entirely and leave the customer at the top. Same pattern as bh-drawer.js.
  const restoreScroll = (y) => {
    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    void root.offsetHeight; // flush the style change before the scroll reads it
    window.scrollTo(0, y);
    root.style.scrollBehavior = previousBehavior;
  };

  const grid = document.getElementById('product-grid');

  // ---------------------------------------------------------------- product page ----
  if (!grid) {
    if (!saved) return;
    const savedUrl = new URL(saved.url, location.origin);
    document.querySelectorAll('.breadcrumbs a').forEach((link) => {
      // Only the crumb for the list they actually came from. A product sits in several
      // collections and can be reached directly, so the breadcrumb often points somewhere
      // else — leave those alone rather than sending the customer where the label doesn't say.
      if (link.pathname !== savedUrl.pathname) return;
      link.href = savedUrl.pathname + savedUrl.search;
      link.addEventListener('click', () => write(RESUME, '1'));
    });
    return;
  }

  // ------------------------------------------------------------------- list page ----
  // pagehide (not unload) is the one that fires reliably on iOS, and it covers bfcache.
  window.addEventListener('pagehide', () => {
    write(KEY, JSON.stringify({ url: location.href, y: window.scrollY }));
  });

  const resuming = read(RESUME) === '1';
  drop(RESUME); // one-shot, so it can't leak into a later fresh visit

  const page = parseInt(new URLSearchParams(location.search).get('page'), 10) || 1;

  // Only restore where we actually left off — a shared or typed ?page=3 link stays page 3.
  if (!saved || saved.url !== location.href) return;

  if (page < 2) {
    // The bare collection URL is reachable from the nav, the hub, a bookmark, anywhere,
    // so a matching entry is NOT on its own proof the customer is returning. Only the
    // breadcrumb sets the resume flag, so only that path scrolls them back down.
    if (resuming && saved.y > 0) requestAnimationFrame(() => restoreScroll(saved.y));
    return;
  }

  if (page > MAX_PAGES) {
    console.info(`[bh-list-restore] page ${page} is past the ${MAX_PAGES}-page cap; not prepending earlier pages.`);
    return;
  }

  history.scrollRestoration = 'manual'; // the document grows under us; we place the scroll below

  const sectionUrl = (n) => {
    const params = new URLSearchParams(location.search);
    params.set('page', n);
    return `${location.pathname}?section_id=${grid.dataset.id}&${params}`;
  };

  const pages = Array.from({ length: page - 1 }, (_, i) => i + 1);

  Promise.all(pages.map((n) => fetch(sectionUrl(n)).then((response) => response.text())))
    .then((documents) => {
      const cards = [];
      documents.forEach((html, index) => {
        const fetched = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
        if (!fetched) return;
        // Rebuild exactly what Load More would have built: page 1 keeps its in-grid promo,
        // later pages contribute products only. The pinned-last card is never prepended —
        // the one already at the end of the landed page is the one that stays.
        const selector = index === 0
          ? '.columns:not(.promotion-column--last)'
          : '.columns:not(.promotion-column)';
        fetched.querySelectorAll(selector).forEach((card) => cards.push(card));
      });
      if (!cards.length) return;

      // The landed page rendered its own copy of the in-grid promo. Page 1's is the
      // canonical one, so drop this page's before prepending — but keep the pinned-last.
      grid.querySelectorAll('.promotion-column:not(.promotion-column--last)')
        .forEach((duplicate) => duplicate.remove());

      grid.prepend(...cards);
      cards.forEach((card) => {
        card.querySelectorAll('.lazyload').forEach((image) => lazySizes.loader.unveil(image));
      });

      requestAnimationFrame(() => restoreScroll(saved.y));
    })
    .catch((error) => console.error('[bh-list-restore] could not rebuild the list:', error));
})();
